import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fetchAbrangenciasSyncCheck, fetchErrosConsecutivos } from '../services/statusDashboardService';
import consumosService, { type ConsumosAlertsResponse } from '../services/consumos';
import {
  CONSECUTIVE_ALERT_KEY,
  CONSECUTIVE_ALERT_PAGE_SIZE,
  CONSUMOS_ALERTS_REFRESH_INTERVAL_MS,
} from '../constants/dashboardAlerts';
import { hasOutdatedDivergence, isIgnoredSyncPlatform, type AbrangenciaCompareResult } from '../utils/abrangenciaCompare';

const ALERTS_REFRESH_INTERVAL_MS = 60_000;

interface SystemAlertsContextValue {
  consecutiveErrorsTotal: number;
  consecutiveErrorsReady: boolean;
  showConsecutiveErrorsAlert: boolean;
  compareResult: AbrangenciaCompareResult | null;
  syncAlertsReady: boolean;
  showOutdatedAlert: boolean;
  showNotDisplayedAlert: boolean;
  consumosAlerts: ConsumosAlertsResponse | null;
  consumosAlertsReady: boolean;
  showLowBreakerBalanceAlert: boolean;
  showNoBreakerBalanceAlert: boolean;
  showNoProxyBalanceAlert: boolean;
}

const SystemAlertsContext = createContext<SystemAlertsContextValue | undefined>(undefined);

export const useSystemAlerts = (): SystemAlertsContextValue => {
  const context = useContext(SystemAlertsContext);
  if (!context) {
    throw new Error('useSystemAlerts must be used within SystemAlertsProvider');
  }
  return context;
};

/** @deprecated Use useSystemAlerts */
export const useDashboardAlerts = useSystemAlerts;

interface SystemAlertsProviderProps {
  children: ReactNode;
}

/**
 * Carrega alertas do sistema uma única vez por sessão autenticada,
 * evitando recarregamento ao navegar entre telas.
 */
export const SystemAlertsProvider: React.FC<SystemAlertsProviderProps> = ({ children }) => {
  const [consecutiveErrorsTotal, setConsecutiveErrorsTotal] = useState(0);
  const [consecutiveErrorsReady, setConsecutiveErrorsReady] = useState(false);
  const [compareResult, setCompareResult] = useState<AbrangenciaCompareResult | null>(null);
  const [syncAlertsReady, setSyncAlertsReady] = useState(false);
  const [consumosAlerts, setConsumosAlerts] = useState<ConsumosAlertsResponse | null>(null);
  const [consumosAlertsReady, setConsumosAlertsReady] = useState(false);
  const isMountedRef = useRef(true);

  const loadConsecutiveErrors = useCallback(async () => {
    try {
      const resp = await fetchErrosConsecutivos({
        key: CONSECUTIVE_ALERT_KEY,
        page: 1,
        pageSize: CONSECUTIVE_ALERT_PAGE_SIZE,
      });

      if (!isMountedRef.current) return;

      if (resp.success) {
        setConsecutiveErrorsTotal(resp.total ?? 0);
      }
    } catch {
      if (!isMountedRef.current) return;
    } finally {
      if (isMountedRef.current) {
        setConsecutiveErrorsReady(true);
      }
    }
  }, []);

  const loadSyncCheck = useCallback(async () => {
    try {
      const resp = await fetchAbrangenciasSyncCheck();
      if (!isMountedRef.current || !resp.success) return;

      setCompareResult({
        productionTotal: resp.productionTotal,
        statusTotal: resp.statusTotal,
        missingInStatus: resp.missingInStatus.filter((row) => !isIgnoredSyncPlatform(row.platform)),
        extraInStatus: (resp.extraInStatus ?? []).filter((row) => !isIgnoredSyncPlatform(row.platform)),
        notDisplayed: resp.notDisplayed.filter((row) => !isIgnoredSyncPlatform(row.platform)),
      });
    } catch {
      if (!isMountedRef.current) return;
    } finally {
      if (isMountedRef.current) {
        setSyncAlertsReady(true);
      }
    }
  }, []);

  const loadConsumosAlerts = useCallback(async () => {
    try {
      const resp = await consumosService.getAlerts();
      if (!isMountedRef.current || !resp.success) return;

      setConsumosAlerts(resp);
    } catch {
      if (!isMountedRef.current) return;
    } finally {
      if (isMountedRef.current) {
        setConsumosAlertsReady(true);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    void Promise.all([loadConsecutiveErrors(), loadSyncCheck(), loadConsumosAlerts()]);

    const refreshTimer = window.setInterval(() => {
      void Promise.all([loadConsecutiveErrors(), loadSyncCheck()]);
    }, ALERTS_REFRESH_INTERVAL_MS);

    const consumosRefreshTimer = window.setInterval(() => {
      void loadConsumosAlerts();
    }, CONSUMOS_ALERTS_REFRESH_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      window.clearInterval(refreshTimer);
      window.clearInterval(consumosRefreshTimer);
    };
  }, [loadConsecutiveErrors, loadSyncCheck, loadConsumosAlerts]);

  const { showOutdatedAlert, showNotDisplayedAlert } = useMemo(() => {
    if (!syncAlertsReady || !compareResult) {
      return { showOutdatedAlert: false, showNotDisplayedAlert: false };
    }

    return {
      showOutdatedAlert: hasOutdatedDivergence(compareResult),
      showNotDisplayedAlert: compareResult.notDisplayed.length > 0,
    };
  }, [compareResult, syncAlertsReady]);

  const value = useMemo<SystemAlertsContextValue>(
    () => ({
      consecutiveErrorsTotal,
      consecutiveErrorsReady,
      showConsecutiveErrorsAlert: consecutiveErrorsReady && consecutiveErrorsTotal > 0,
      compareResult,
      syncAlertsReady,
      showOutdatedAlert,
      showNotDisplayedAlert,
      consumosAlerts,
      consumosAlertsReady,
      showLowBreakerBalanceAlert:
        consumosAlertsReady && Boolean(consumosAlerts?.showLowBreakerBalanceAlert),
      showNoBreakerBalanceAlert:
        consumosAlertsReady && Boolean(consumosAlerts?.showNoBreakerBalanceAlert),
      showNoProxyBalanceAlert:
        consumosAlertsReady && Boolean(consumosAlerts?.showNoProxyBalanceAlert),
    }),
    [
      compareResult,
      consecutiveErrorsReady,
      consecutiveErrorsTotal,
      consumosAlerts,
      consumosAlertsReady,
      showNotDisplayedAlert,
      showOutdatedAlert,
      syncAlertsReady,
    ],
  );

  return (
    <SystemAlertsContext.Provider value={value}>
      {children}
    </SystemAlertsContext.Provider>
  );
};

/** @deprecated Use SystemAlertsProvider */
export const DashboardAlertsProvider = SystemAlertsProvider;
